import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const WHATSAPP_CALL_MODAL_LOCK = 'redux_modal_lock';

const LOCK_POLL_MS = 1000;

const canUseWebLocks = isMobileSystem => (
    !isMobileSystem
    && 'locks' in navigator
    && typeof navigator.locks?.request === 'function'
);

export const useSingleTabModal = (isModalOpen, hideWhatsappCallingModal) => {
    const dispatch = useDispatch();
    const isMobileSystem = useSelector(state => state.common.user.isMobileSystem);

    const lockResolverRef = useRef(null);

    useEffect(() => {
        // If the modal isn't open in this tab, release any active lock and exit
        if (!isModalOpen) {
            if (lockResolverRef.current) {
                lockResolverRef.current();
                lockResolverRef.current = null;
            }
            return;
        }

        // Native app: un solo WebView, no hay otras pestañas. Web Locks en
        // WKWebView / Android WebView puede devolver lock=null y cerrar el modal.
        if (!canUseWebLocks(isMobileSystem)) {
            return;
        }

        // Attempt to acquire an exclusive lock non-blockingly
        navigator.locks.request(
            WHATSAPP_CALL_MODAL_LOCK,
            { ifAvailable: true },
            async (lock) => {
                if (lock === null) {
                    // Lock was NOT acquired because another tab holds it
                    console.log('This modal is already open in another tab.');

                    // Revert Redux state back to false in this tab
                    dispatch(hideWhatsappCallingModal());
                    return;
                }

                // Lock WAS acquired! Hold it until the promise resolves
                await new Promise((resolve) => {
                    lockResolverRef.current = resolve;
                });
            }
        );

        return () => {
            if (lockResolverRef.current) {
                lockResolverRef.current();
                lockResolverRef.current = null;
            }
        };
    }, [isModalOpen, dispatch, hideWhatsappCallingModal, isMobileSystem]);
};

export const useWhatsappCallActiveInBrowser = () => {
    const [isActive, setIsActive] = useState(false);
    const isMobileSystem = useSelector(state => state.common.user.isMobileSystem);

    useEffect(() => {
        if (!canUseWebLocks(isMobileSystem) || typeof navigator.locks.query !== 'function') {
            return;
        }

        let cancelled = false;

        const sync = async () => {
            try {
                const snapshot = await navigator.locks.query();
                const held = snapshot.held?.some(lock => lock.name === WHATSAPP_CALL_MODAL_LOCK) ?? false;
                if (!cancelled) setIsActive(held);
            } catch {
                // ignore
            }
        };

        sync();
        const intervalId = setInterval(sync, LOCK_POLL_MS);
        window.addEventListener('focus', sync);
        document.addEventListener('visibilitychange', sync);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            window.removeEventListener('focus', sync);
            document.removeEventListener('visibilitychange', sync);
        };
    }, [isMobileSystem]);

    return isActive;
};
